import type { Inbox as InboxData, InboxItem, InboxReopen } from "@fieldpath/shared";
import { mdyDay } from "@fieldpath/shared";
import { Fragment, useCallback, useEffect, useState } from "react";

import { api, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { navigate, navigateToPath } from "../router";
import { ruleName } from "../ruleActors";
import { HelpButton } from "./HelpButton";
import { KindTag } from "./KindTag";
import { useTaskHover } from "./TaskHoverCard";
import type { TaskHoverBind } from "./TaskHoverCard";

/**
 * Everything addressed to you, unread first.
 *
 * Mentions were a dropdown, which was fine while they were the only kind of
 * alert. Once work arriving and being assigned also need telling, a dropdown
 * becomes a place things scroll out of — so this is a page, with read items
 * kept rather than discarded, because "what was I told last week" is a real
 * question.
 */
export function Inbox({
  fetcher,
  onChanged,
}: {
  fetcher: Fetcher;
  onChanged: () => void;
}) {
  // One hook per screen: `bind` goes on each row, `card` renders the portal.
  const { bind, card } = useTaskHover(fetcher);
  const [data, setData] = useState<InboxData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    void api
      .inbox(fetcher)
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, [fetcher]);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(load, [load, back]);

  const open = async (item: InboxItem): Promise<void> => {
    // Reading it is the act of opening it — an inbox that needs a separate
    // "mark read" click is one people stop maintaining.
    if (item.readAt === null) {
      await api.markInboxRead(fetcher, [item.id]).catch(() => undefined);
      onChanged();
    }
    if (item.taskId) {
      navigate({ kind: "task", id: item.taskId });
      return;
    }
    // A requested DELIVERABLE (F140): the notice is about it, not a task.
    if (item.deliverableId) {
      navigate({ kind: "deliverable", id: item.deliverableId });
      return;
    }
    // WHAT'S NEW (F149): a place in Help, in this app -- `/help/E23#v1-1`, the only shape the
    // database lets this kind carry (`notifications_source_shape`).
    if (item.kind === "whats_new") {
      if (item.linkUrl) navigateToPath(item.linkUrl);
      return;
    }
    /*
     * A scheduler notification goes to the SCHEDULER. Same tab, for the same
     * reason the nav link is: it is another application, not a view inside
     * this one, and opening it in an iframe or a new tab would pretend
     * otherwise.
     *
     * `link_url` is checked against an allowlist of the scheduler's addresses
     * when the row is WRITTEN, because this line sends people to it — an
     * endpoint that accepts any link and puts it in front of people is a
     * phishing primitive with a Microsoft-authenticated envelope. So this is
     * safe to follow, and the check that makes it safe is deliberately at the
     * other end. (The email puts the same link behind its button, and checks
     * it again first: `isSchedulerLink`, used by `api/src/mail/render.ts`.)
     */
    if (item.linkUrl) window.location.assign(item.linkUrl);
  };

  /*
   * THE ONE-CLICK REOPEN (Hibernation 3b). The offer said "reopen it from
   * its page"; this is that same PM-or-admin write, from where the offer landed.
   * Reading the offer is part of acting on it, so it is marked read too.
   */
  const [reopening, setReopening] = useState<string | null>(null);
  const [reopenError, setReopenError] = useState<{ id: string; message: string } | null>(null);
  const reopen = async (item: InboxItem, offer: InboxReopen): Promise<void> => {
    setReopening(item.id);
    setReopenError(null);
    try {
      await api.setProjectCompleted(fetcher, offer.projectId, false);
      if (item.readAt === null) {
        await api.markInboxRead(fetcher, [item.id]).catch(() => undefined);
      }
      onChanged();
      load();
    } catch (e) {
      setReopenError({ id: item.id, message: (e as Error).message });
    } finally {
      setReopening(null);
    }
  };

  const rowFor = (i: InboxItem, muted: boolean) => (
    <Fragment key={i.id}>
      <InboxRow item={i} onOpen={open} hover={bind} muted={muted} />
      {i.reopen ? (
        <ReopenAction
          offer={i.reopen}
          unread={!muted}
          busy={reopening === i.id}
          error={reopenError?.id === i.id ? reopenError.message : null}
          onReopen={() => void reopen(i, i.reopen!)}
        />
      ) : null}
    </Fragment>
  );

  const markAll = async (): Promise<void> => {
    await api.markInboxRead(fetcher).catch(() => undefined);
    onChanged();
    load();
  };

  if (error) return <div className="error">{error}</div>;
  if (!data) return <p className="muted">Loading…</p>;

  const unread = data.items.filter((i) => i.readAt === null);
  const read = data.items.filter((i) => i.readAt !== null);

  return (
    <>
      {card}
      <div className="count-line">
        {unread.length} UNREAD · {data.items.length} TOTAL
      </div>
      <div className="row spread" style={{ marginBottom: 20 }}>
        <h1 className="page-h1" style={{ margin: 0 }}>
          Inbox
          <HelpButton screens="inbox" />
        </h1>
        {unread.length > 0 ? (
          <button className="secondary" onClick={() => void markAll()}>
            Mark all read
          </button>
        ) : null}
      </div>

      <div className="section-label" style={{ marginTop: 0 }}>
        <span>Unread</span>
        <span className="count">{unread.length}</span>
      </div>

      {unread.length === 0 ? (
        <p className="muted">Nothing waiting for you.</p>
      ) : (
        unread.map((i) => rowFor(i, false))
      )}

      {read.length > 0 ? (
        <>
          <div className="section-label">
            <span>Earlier</span>
            <span className="count">already read</span>
          </div>
          {read.map((i) => rowFor(i, true))}
        </>
      ) : null}
    </>
  );
}

/** The sentence, by kind. Says what happened, not which table it came from. */
function headline(item: InboxItem): string {
  // A rule is named as itself ("The resource scheduler assigned you to …"), and
  // "Someone" is left for a person with no Deltek record (ruleActors.ts).
  const who = item.actorName ?? (item.byRule ? ruleName(item.byRule) : "Someone");
  switch (item.kind) {
    case "mention":
      return `${who} mentioned you`;
    case "stage_arrived":
      return item.stageName
        ? `The work reached ${item.stageName}, which is yours`
        : "Work reached a stage you hold";
    case "assigned":
      return item.stageName
        ? `${who} assigned you to ${item.stageName}`
        : `${who} assigned you`;
    case "owner":
      return `${who} made you the PM / Task Manager`;
    // Raised by the RESOURCE SCHEDULER. Its subject is already a complete
    // sentence -- "Approved - Tia Tech" -- written by the application that
    // knows what happened, so repeating the kind here would say it twice.
    //
    // These fell off the end of this switch until 7 Oct 2026 and returned
    // undefined, so every scheduler notification rendered with an EMPTY
    // headline. Widening NotificationKind is what made the compiler point at
    // this function.
    case "approval_needed":
    case "approval_decided":
      return item.subject;
    // NO `who`. A rule released this, not a person, so the row carries no
    // actor and `who` would render as "Someone" -- which reads as though
    // somebody did it and we failed to find out their name. The sentence
    // describes the EVENT instead, which is what actually happened.
    case "block_released":
      return item.stageName
        ? `${item.stageName} is no longer blocked`
        : "Work you were waiting on is no longer blocked";
    // No `who` either: the sweep did it, after the block had been open past
    // the threshold. The detail says how long.
    case "hibernated":
      return item.stageName
        ? `${item.stageName} has been hibernated`
        : "Blocked work has been hibernated";
    // The detail names the project and says what to do; the headline only
    // has to say why this landed here.
    case "reopen_offered":
      return "Work is moving again in a project marked Complete";
    case "date_change_requested":
      return `${who} asks to move a date`;
    case "date_change_decided":
      return `${who} answered your date request`;
    // Batched (E1): the detail lists the items. The person is named only
    // when one person did all of it -- otherwise the event, not a guess.
    case "item_completed":
      return item.actorName
        ? `${item.actorName} marked items done${item.stageName ? ` on ${item.stageName}` : ""}`
        : `Items marked done${item.stageName ? ` on ${item.stageName}` : ""}`;
    case "item_correction":
      return `Items${item.stageName ? ` on ${item.stageName}` : ""} are no longer done`;
    // The subject is the sentence: "Canopy V1.1 is out - see what changed".
    case "whats_new":
      return item.subject;
    // Who put it on hold; the detail says why (F102).
    case "stage_blocked":
      return item.stageName ? `${who} blocked ${item.stageName}` : `${who} blocked your stage`;
    // Requested work (F140), in Jeremy's words. The title is the row's second
    // line; a rejection's reason is its detail.
    case "work_requested":
      return `${who} requested ${item.taskTitle ?? item.deliverableName ?? item.subject}`;
    case "request_approved":
      return `${who} approved your request`;
    case "request_rejected":
      return `${who} rejected your request`;
  }
}

function InboxRow({
  item,
  onOpen,
  muted,
  hover,
}: {
  hover: TaskHoverBind;
  item: InboxItem;
  onOpen: (item: InboxItem) => void;
  muted?: boolean;
}) {
  // ONLY Fieldpath's own notifications can have a deleted task. A scheduler
  // notification has no task BY CONSTRUCTION -- migration 029 constrains it to
  // `task_id IS NULL AND link_url IS NOT NULL` -- so reading a null task as
  // "deleted" disabled every one of them: unclickable, unreadable, and
  // claiming a task had been deleted that never existed.
  const fromScheduler = item.source === "scheduler";
  // Nor does a What's new notice (F149): it is about a version of Canopy, and points at Help.
  const whatsNew = item.kind === "whats_new";
  // About a deliverable rather than a task (a requested deliverable, F140).
  const aboutDeliverable = item.taskId === null && item.deliverableId !== null;
  const gone =
    !fromScheduler &&
    !whatsNew &&
    (aboutDeliverable ? item.deliverableName === null : item.taskId === null || item.taskTitle === null);

  return (
    <button
      className={`work inbox-row ${muted ? "" : "unread"} ${item.reopen ? "has-action" : ""}`}
      onClick={() => void onOpen(item)}
      disabled={gone}
      title={gone ? "That task has been deleted" : undefined}
      // A deleted task has nothing to preview, and asking for it would be one
      // guaranteed 404 per hover.
      {...(gone || !item.taskId
        ? {}
        : hover(item.taskId, item.taskTitle ?? item.subject))}
    >
      {/* An 8px dot rather than bold-everything. Unread is a STATE of the
          row, and marking it on the row's edge leaves the headline free to be
          read at its own weight. */}
      {muted ? null : <span className="unread-dot" aria-label="unread" />}
      <div className="work-main">
        <div className="work-title">{headline(item)}</div>
        <div className="work-where">
          {/* The tag says what KIND of thing this is about. A scheduler
              notification is not about a task, and tagging it TSK was telling
              people it was. It gets a plain mono label instead -- deliberately
              not a KindTag, because Fieldpath's three kinds are project,
              deliverable and task, and this is none of them. */}
          {fromScheduler ? (
            <span className="inbox-from">SCHEDULER</span>
          ) : whatsNew ? (
            <span className="inbox-from">WHAT'S NEW</span>
          ) : aboutDeliverable ? (
            <KindTag kind="deliverable" size="row" />
          ) : (
            <KindTag kind="task" size="row" />
          )}
          {/* The title as it stands now, falling back to what it was called
              when the alert was raised. A scheduler notification has no second
              line to put here: its subject is already the headline and its
              detail renders below, so repeating either would say it twice. */}
          {fromScheduler
            ? null
            : whatsNew
              ? "Help"
              : aboutDeliverable
                ? (item.deliverableName ?? item.subject)
                : (item.taskTitle ?? item.subject)}
          {gone ? <span className="muted"> · deleted</span> : null}
        </div>
        {item.detail ? <div className="inbox-detail">{item.detail}</div> : null}
      </div>
      <div className="work-when muted">
        <div className="when-big" style={{ fontSize: 12.5 }}>
          {ago(item.createdAt)}
        </div>
      </div>
    </button>
  );
}

/**
 * Under a reopen offer: the button, or why there is none. A sibling of the
 * row rather than inside it, because the row is itself a button and a button
 * cannot hold another.
 *
 * It reads the project AS IT IS NOW, not as it was when the offer was raised:
 * an offer somebody already acted on says so, rather than offering to reopen
 * an open project.
 */
function ReopenAction({
  offer,
  unread,
  busy,
  error,
  onReopen,
}: {
  offer: InboxReopen;
  unread: boolean;
  busy: boolean;
  error: string | null;
  onReopen: () => void;
}) {
  return (
    <div className={`inbox-action ${unread ? "unread" : ""}`}>
      {!offer.complete ? (
        <span className="muted">{offer.projectName} has been reopened.</span>
      ) : offer.mayReopen ? (
        <>
          <button className="secondary" onClick={onReopen} disabled={busy}>
            {busy ? "Reopening…" : `Reopen ${offer.projectName}`}
          </button>
          {error ? <span className="error-inline">{error}</span> : null}
        </>
      ) : (
        <span className="muted">
          Only a project manager or an admin can reopen {offer.projectName} —
          ask one to, from the project's page.
        </span>
      )}
    </div>
  );
}

function ago(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return mdyDay(iso);
}
